import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ChartTooltipShell } from "../panel";
import { monthYear } from "./chart-date";
import { formatCompactCurrency } from "../../../pages/PortfolioPerformance/lib/format";

const AXIS_COLOR = "var(--neutral-inactive)";

export interface BarSeries {
  dataKey: string;
  name: string;
  color: string;
}

function GroupedTooltip({
  active,
  payload,
  label,
  suffix,
  currency,
}: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string;
  suffix: string;
  currency?: boolean;
}) {
  if (!active || !payload?.length) return null;
  return (
    <ChartTooltipShell>
      <p className="font-semibold text-foreground">{label !== undefined ? monthYear(label) : label}</p>
      <div className="mt-1 space-y-0.5">
        {payload.map((entry) => (
          <p key={entry.name} className="flex items-center gap-2 text-muted-foreground">
            <span className="size-2" style={{ backgroundColor: entry.color }} aria-hidden />
            {entry.name}
            <span className="ml-auto tabular-nums text-foreground">
              {currency ? formatCompactCurrency(entry.value) : `${entry.value.toFixed(2)}${suffix}`}
            </span>
          </p>
        ))}
      </div>
    </ChartTooltipShell>
  );
}

export function GroupedBarChart<T extends Record<string, unknown>>({
  data,
  categoryKey,
  series,
  suffix = "%",
  currency,
  height = 340,
  layout = "vertical-bars",
  categoryWidth = 160,
  onBarClick,
  stacked = false,
  segmentGap = false,
}: {
  data: T[];
  categoryKey: string;
  series: BarSeries[];
  suffix?: string;
  /** Values are raw dollars: ticks and tooltip show $1.2K / $737.5M / $1.25B and ignore suffix. */
  currency?: boolean;
  height?: number;
  /** "vertical-bars" = bars grow up; "horizontal-bars" = bars grow right */
  layout?: "vertical-bars" | "horizontal-bars";
  categoryWidth?: number;
  /** When set, bars become clickable and receive the row behind them. */
  onBarClick?: (row: T, dataKey: string) => void;
  /** Stack every series into one bar per category instead of grouping side by side. */
  stacked?: boolean;
  /** Stacked bars only: a 2px line in the card colour between neighbouring segments, so same-family colours stay apart. */
  segmentGap?: boolean;
}) {
  const horizontal = layout === "horizontal-bars";
  const tick = (value: number) => (currency ? formatCompactCurrency(value) : `${value.toFixed(1)}${suffix}`);

  return (
    <div style={{ height }} className="w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          layout={horizontal ? "vertical" : "horizontal"}
          margin={{ top: 8, right: 16, bottom: 0, left: 0 }}
          barGap={2}
          // "sign" stacks negatives down from zero instead of on top of the positive bars.
          {...(stacked ? { stackOffset: "sign" as const } : {})}
        >
          <CartesianGrid
            stroke="var(--border)"
            strokeDasharray="2 4"
            vertical={horizontal}
            horizontal={!horizontal}
          />
          <XAxis
            type={horizontal ? "number" : "category"}
            tick={{ fill: AXIS_COLOR, fontSize: 11 }}
            stroke={AXIS_COLOR}
            tickLine={false}
            {...(horizontal
              ? { tickFormatter: tick }
              : { dataKey: categoryKey, tickFormatter: monthYear })}
          />
          <YAxis
            type={horizontal ? "category" : "number"}
            width={horizontal ? categoryWidth : 56}
            tick={{ fill: AXIS_COLOR, fontSize: 11 }}
            stroke={AXIS_COLOR}
            tickLine={false}
            {...(horizontal
              ? { dataKey: categoryKey, tickFormatter: monthYear }
              : { tickFormatter: tick })}
          />
          <Tooltip
            content={<GroupedTooltip suffix={suffix} currency={currency} />}
            cursor={{ fill: "var(--border)", fillOpacity: 0.25 }}
          />
          {series.length > 1 ? (
            <Legend
              wrapperStyle={{ fontSize: 11, color: "var(--muted-foreground)" }}
              iconType="square"
            />
          ) : null}
          {series.map((bar) => (
            <Bar
              key={bar.dataKey}
              dataKey={bar.dataKey}
              name={bar.name}
              fill={bar.color}
              {...(stacked ? { stackId: "stack" } : {})}
              {...(stacked && segmentGap ? { stroke: "hsl(var(--card))", strokeWidth: 2 } : {})}
              isAnimationActive={false}
              {...(onBarClick
                ? {
                    cursor: "pointer",
                    onClick: (entry: unknown) => {
                      const payload = (entry as { payload?: T })?.payload;
                      if (payload) onBarClick(payload, bar.dataKey);
                    },
                  }
                : {})}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
